import { projectTypes, projectTypesInversed, ProjectTypesInversedKeys, ProjectTypesKeys } from '@teinor/erp/types/company/customer/customerProject'
import { connect } from 'react-redux'
import { useCallback, useMemo, useState } from 'react'
import { ConnectedProps } from 'react-redux'
import { MediumModalCreateEdit } from '../../../../../../../../../baseComponents/ModalsLayouts/ModalCreateEdit'
import { ModalEditComponentProps } from '../../../../../../../../../baseComponents/ModalsLayouts/types'
import { FormRender } from '../../../../../../../../../forms/FormRenderer/types'
import { sortCustomerProjectStatuses } from '../../../../../../../../../specificComponents/Customers/SortCustomerProjectStatuses'
import withLoading from '../../../../../../../../../utils/hoc/withLoading'
import useTwinTranslation from '../../../../../../../../../utils/hooks/useTwinTranslation'
import { AllReduxPayloads } from '../../../../../../../../../utils/reducers'
import { EmployeesPayload } from '../../../../../../../../../utils/reducers/company/employees'
import { CustomerPayload } from '../../../../../../../../../utils/reducers/customers/customer'
import { CustomerProjectTypePayload } from '../../../../../../../../../utils/reducers/customers/customerProjectTypes'
import { GroupPayload } from '../../../../../../../../../utils/reducers/groups/groups'
import CustomSelectSectionCustomers from '../../../../../../../../../specificComponents/Customers/CustomSelectSectionCustomers'
import { addDictionaryExtraLine, TwinDictionary } from '../../../../../../../../../utils/globals/dictionary'
import { CustomerProjectAddModelTypeExtended } from '../../../../CustomerProjects/types'
import { emptyExtraLineFirstSortFunction } from '../../../../../../../../../forms/CustomSelect/functions'
import { TChecklistsPayload } from '../../../../../../../../../utils/reducers/customers/tChecklists'

type ModalCreateCustomerProjectFlowProps = ModalEditComponentProps & ReduxCustomerProject & {
    customerProjectTypeId: number
    projectType: ProjectTypesKeys
}

const ModalCreateCustomerProjectFlow: React.FC<ModalCreateCustomerProjectFlowProps> = ({ customerProjectTypeId, onSubmit, setOpened, employees, groups, customers, customerProjectTypes, projectType, tChecklists }) => {
    const { t } = useTwinTranslation()
    const { myProjectStatuses, setMyProjectStatusId } = useModalCreateCustomerProjectFlowLogic({ id: customerProjectTypeId, projectType, customerProjectTypes })
    const checkListExtraLineDict = useMemo(() => addDictionaryExtraLine(JSON.parse(JSON.stringify(tChecklists || {}))), [tChecklists])
    const translations: Record<ProjectTypesKeys, {title: string, button: string}> = {
        0: {
            title: t('createOpportunity', 'Crear oportunidad'),
            button: t('create', 'Crear')
        },
        1: {
            title: t('createProject', 'Crear proyecto'),
            button: t('create', 'Crear')
        }
    }
    const fields: FormRender<CustomerProjectAddModelTypeExtended> = [
        {
            cols: 2,
            elements: [
                {
                    name: 'name',
                    component: 'InputWithLabelMargin',
                    label: t('name', 'Nombre'),
                    required: true
                },
                {
                    name: 'description',
                    component: 'TextArea',
                    label: t('description', 'Descripción'),
                },
                {
                    name: 'CustomerId',
                    component: 'Custom',
                    render: ({extraProps}) => <CustomSelectSectionCustomers name='CustomerId' customers={extraProps.customers} label={t('customer', 'Cliente')} required={true} />,
                    extraProps: { customers }
                },
                {
                    name: 'CustomerProjectTypeId',
                    component: 'CustomSelect',
                    label: (projectType === projectTypesInversed['project']) ? t('projectType', 'Tipo de proyecto') : t('opportunityType', 'Tipo de oportunidad'),
                    items: customerProjectTypes || {},
                    required: true,
                    readOnly: true,
                    value: customerProjectTypeId,
                    onChange: (value) => setMyProjectStatusId(value)
                },
                {
                    name: 'CurrentCProjectStatusId',
                    component: 'CustomSelectColor',
                    label: t('projectStatus', 'Tipo de estado'),
                    items: myProjectStatuses || [],
                    value: customerProjectTypes?.[customerProjectTypeId]?.CustomerProjectStatusesByType?.[projectTypes[projectType as ProjectTypesKeys] as ProjectTypesInversedKeys]?.[0]?.id || '',
                    required: true,
                    extraDropDownParams: {
                        sortFunction: sortCustomerProjectStatuses
                    }
                },
                {
                    name: 'TChecklistId',
                    component: 'CustomSelectWithSearchBar',
                    label: t('defaultList', 'Lista por defecto'),
                    items: checkListExtraLineDict || {},
                    value: customerProjectTypes?.[customerProjectTypeId || 0]?.CPChecklistId,
                    sortFunction: emptyExtraLineFirstSortFunction
                },
                {
                    name: 'limit_date',
                    label: t('limitDate', 'Fecha límite'),
                    component: 'InputCalendarStateFull',
                    onlyValids: true,
                },
                {
                    name: 'projectType',
                    component: 'InputHidden',
                    value: projectType
                }
            ]
        },
        {
            cols: 1,
            title: t('employees', 'Empleados'),
            elements: [
                {
                    name: 'Employees',
                    component: 'MultipleCheckboxSelector',
                    subtitles: { subtitleOptions: t('notSelected', 'Sin seleccionar'), subtitleSelectedOptions: t('selected', 'Seleccionados') },
                    items: employees || {},
                }
            ]
        },
        {
            cols: 1,
            title: t('groups', 'Grupos'),
            elements: [
                {
                    name: 'Groups',
                    component: 'MultipleCheckboxSelector',
                    subtitles: { subtitleOptions: t('notSelected', 'Sin seleccionar'), subtitleSelectedOptions: t('selected', 'Seleccionados') },
                    items: groups || {},
                }
            ]
        }
    ]
    return (
        <MediumModalCreateEdit url='/api/app/customer/customerProject/createCustomerProject' fields={fields} setOpened={setOpened} onSubmit={onSubmit} translations={translations[projectType]} haveButtonPermissions={true}>
        </MediumModalCreateEdit>
    )
}

interface ModalCreateCustomerProjectFlowLogicProps {
    id: number
    projectType: ProjectTypesKeys
    customerProjectTypes: CustomerProjectTypePayload
}

const useModalCreateCustomerProjectFlowLogic = ({ id, projectType, customerProjectTypes }: ModalCreateCustomerProjectFlowLogicProps) => {
    const idProjectType = id !== undefined ? String(id) : ''
    const [myProjectStatusId, setMyProjectStatusId] = useState<string>(idProjectType)
    const myProjectStatuses = myProjectStatusId ? customerProjectTypes?.[parseInt(myProjectStatusId)].customerProjectStatusDict[projectTypes[projectType as ProjectTypesKeys] as ProjectTypesInversedKeys] : []
    const sortProjectStatusesFn = useCallback((items: TwinDictionary) => sortCustomerProjectStatuses(items), [])

    return { customerProjectTypes, myProjectStatuses, setMyProjectStatusId, sortProjectStatusesFn }
}


const customerProjectDispatch = {
    setCustomerProjectTypes: (payload: CustomerProjectTypePayload) => ({ type: 'CHANGE_CUSTOMER_PROJECT_TYPES', payload }),
    setGroups: (payload: GroupPayload) => ({ type: 'CHANGE_GROUP', payload }),
    setEmployees: (payload: EmployeesPayload) => ({ type: 'CHANGE_EMPLOYEE', payload }),
    setCustomers: (payload: CustomerPayload) => ({ type: 'CHANGE_CUSTOMER', payload }),
    setTChecklists: (payload: TChecklistsPayload) => ({ type: 'CHANGE_T_CHECKLISTS', payload })
}

export type ReduxCustomerProject = ConnectedProps<typeof customerProjectConnect>
const mapCustomerProjectTypesConnector = (state: AllReduxPayloads) => ({ customerProjectTypes: state.customerProjectTypes, groups: state.groups, employees: state.employees, customers: state.customers, tChecklists: state.tChecklists })
const customerProjectConnect = connect(mapCustomerProjectTypesConnector, customerProjectDispatch)

const customerProjectConnectLoading = withLoading(ModalCreateCustomerProjectFlow, [{ fetchUrl: '/api/app/customer/customerProject/customerProjectTypes/getAllCustomerProjectTypesFull', propName: 'customerProjectTypes', setFunctionName: 'setCustomerProjectTypes' }, { fetchUrl: '/api/app/group/getAllGroupsListing', propName: 'groups', setFunctionName: 'setGroups' }, { fetchUrl: '/api/app/employee/getAllEmployees', propName: 'employees', setFunctionName: 'setEmployees' }, { fetchUrl: '/api/app/customer/getAllCustomersComplete', propName: 'customers', setFunctionName: 'setCustomers' }, { fetchUrl: '/api/app/config/templateChecklist/getAllTChecklists', propName: 'tChecklists', setFunctionName: 'setTChecklists' }])

const ModalCreateCustomerProjectsConnect = customerProjectConnect(customerProjectConnectLoading)

export default ModalCreateCustomerProjectsConnect